<template lang="pug">
.purchase-invoice-item-review

  section
    el-header
      el-col(:span="16")
        el-page-header(@back="back" content="Conciliação das notas de entrada")
      el-col(:span="8" style="text-align: right")
        el-button(icon="el-icon-refresh" :loading="rematching" @click="rematch") Tentar vincular novamente

    el-tabs(v-model="tab")
      el-tab-pane(label="Itens sem produto" name="pending")
        p.hint
          | Itens de compra e bonificação que não foram vinculados automaticamente (pelo código de barras ou por um código
          | de fornecedor já conhecido). Ao vincular, o código do fornecedor é lembrado e os outros itens pendentes com o
          | mesmo código são vinculados junto.

        snowflake-search-list(ref="list" context="purchase-invoice-item" :service="pendingService" :page-size="20"
          :action="false" :details="false" :remove="false" :table-row-class-name="() => ''" fixed-operations
          :operations-width="180")
          template(slot="tableRows")
            el-table-column(label="Emissão" width="100")
              template(slot-scope="scope") {{ formatDate(scope.row.issuedAt) }}
            el-table-column(label="NF" width="90")
              template(slot-scope="scope")
                router-link(:to="{ name: 'purchase-invoice-detail', params: { id: scope.row.item.purchaseInvoiceId } }") {{ scope.row.invoiceNumber }}
            el-table-column(prop="issuerName" label="Fornecedor" min-width="160" show-overflow-tooltip)
            el-table-column(label="Código" width="110" show-overflow-tooltip)
              template(slot-scope="scope") {{ scope.row.item.supplierCode }}
            el-table-column(label="EAN" width="130")
              template(slot-scope="scope") {{ scope.row.item.ean }}
            el-table-column(label="Descrição" min-width="220" show-overflow-tooltip)
              template(slot-scope="scope") {{ scope.row.item.description }}
            el-table-column(label="NCM" width="95")
              template(slot-scope="scope") {{ formatNcm(scope.row.item.ncm) }}
            el-table-column(label="Quantidade" width="110" align="right")
              template(slot-scope="scope") {{ formatNumber(scope.row.item.quantity) }} {{ scope.row.item.unit }}
            el-table-column(label="Valor unit." width="110" align="right")
              template(slot-scope="scope") {{ formatCurrency(scope.row.item.unitPrice) }}
          template(slot="rowActions" slot-scope="{ scope }")
            el-tooltip(content="Vincular a um produto" placement="top")
              el-button(size="mini" type="primary" plain icon="el-icon-link" @click.stop="openMatchDialog(scope.row)")
            el-tooltip(content="Não é produto de estoque" placement="top")
              el-button(size="mini" plain icon="el-icon-close" @click.stop="ignore(scope.row)")
            el-tooltip(content="Desconsiderar item" placement="top")
              el-button(size="mini" plain icon="el-icon-remove-outline" @click.stop="disregard(scope.row)")

      el-tab-pane(label="NCM divergente" name="ncm")
        p.hint
          | Produtos cujo NCM é diferente do informado na nota de compra mais recente. Usar o NCM da nota também
          | aplica o CEST da nota, já que o CEST depende do NCM; os dois são usados nas notas de saída. Um CEST de
          | venda porta a porta (28.xxx.xx) não é aplicado e precisa ser ajustado no cadastro do produto. Quando a nota
          | é que está errada, manter o NCM atual tira o produto da lista até uma nota informar outro NCM.

        el-table(ref="divergenceTable" :data="divergences" v-loading="loadingDivergences" size="mini")
          el-table-column(label="Produto" min-width="220" show-overflow-tooltip)
            template(slot-scope="scope")
              router-link(:to="{ name: 'product-detail', params: { id: scope.row.productId } }") {{ scope.row.productId }} · {{ scope.row.productName }}
          el-table-column(label="NCM do produto" width="130")
            template(slot-scope="scope") {{ formatNcm(scope.row.productNcm) }}
          el-table-column(label="NCM da nota" width="130")
            template(slot-scope="scope") {{ formatNcm(scope.row.invoiceNcm) }}
          el-table-column(label="CEST da nota" width="110")
            template(slot-scope="scope") {{ formatCest(scope.row.invoiceCest) || 'Sem CEST' }}
          el-table-column(label="NF" width="90")
            template(slot-scope="scope")
              router-link(:to="{ name: 'purchase-invoice-detail', params: { id: scope.row.purchaseInvoiceId } }") {{ scope.row.invoiceNumber }}
          el-table-column(prop="issuerName" label="Fornecedor" min-width="160" show-overflow-tooltip)
          el-table-column(label="Emissão" width="100")
            template(slot-scope="scope") {{ formatDate(scope.row.issuedAt) }}
          el-table-column(width="130" align="center" fixed="right")
            template(slot-scope="scope")
              el-tooltip(content="Usar NCM e CEST da nota" placement="top")
                el-button(size="mini" type="primary" plain icon="el-icon-check" :loading="applyingNcm === scope.row.productId"
                  @click="useInvoiceNcm(scope.row)")
              el-tooltip(content="Manter NCM atual" placement="top")
                el-button(size="mini" plain icon="el-icon-close" :loading="keepingNcm === scope.row.productId"
                  @click="keepProductNcm(scope.row)")

  el-dialog(title="Vincular item a um produto" :visible.sync="dialog.visible" width="700px")
    template(v-if="dialog.row")
      el-descriptions(:column="2" size="small" border)
        el-descriptions-item(label="Fornecedor") {{ dialog.row.issuerName }}
        el-descriptions-item(label="NF") {{ dialog.row.invoiceNumber }}
        el-descriptions-item(label="Descrição" :span="2") {{ dialog.row.item.description }}
        el-descriptions-item(label="Código") {{ dialog.row.item.supplierCode }}
        el-descriptions-item(label="EAN") {{ dialog.row.item.ean || '-' }}
        el-descriptions-item(label="Quantidade") {{ formatNumber(dialog.row.item.quantity) }} {{ dialog.row.item.unit }}
        el-descriptions-item(label="Valor unit.") {{ formatCurrency(dialog.row.item.unitPrice) }}

      h5 Sugestões
      p.hint(v-if="!dialog.loadingSuggestions && !dialog.suggestions.length")
        | Nenhuma sugestão: a nota não tem pedido vinculado com produtos ainda livres e nenhum produto tem este código de barras.
      .suggestions(v-loading="dialog.loadingSuggestions")
        el-radio(v-for="suggestion in dialog.suggestions" :key="suggestion.productId" v-model="dialog.productId"
          :label="suggestion.productId" @change="pickSuggestion(suggestion)")
          | {{ productLabel({ id: suggestion.productId, name: suggestion.productName, brandName: suggestion.brandName }) }}
          span.suggestion-detail {{ describeSuggestion(suggestion) }}

      h5 Produto
      el-select.stretched(v-model="dialog.productId" filterable remote :remote-method="searchProducts"
        :loading="dialog.searching" placeholder="Busque por nome, ID ou código de barras")
        el-option(v-for="product in dialog.productOptions" :key="product.id" :value="product.id" :label="productLabel(product)")
          span {{ product.id }} · {{ product.name }}
          span.option-brand {{ product.brandName }}

      el-form.units(label-position="top")
        el-form-item(:label="`Unidades do produto em cada ${dialog.row.item.unit} da nota`")
          el-input-number(v-model="dialog.units" :min="1" :precision="0" controls-position="right")
        .option
          el-checkbox(v-model="dialog.remember")
            | Lembrar para o código {{ dialog.row.item.supplierCode }} deste fornecedor e vincular os itens pendentes com ele
        .option(v-if="dialog.row.item.ean")
          el-checkbox(v-model="dialog.addBarcode" :disabled="!barcodeStatus.eligible")
            | Adicionar o código de barras {{ dialog.row.item.ean }} ao produto e vincular os itens pendentes com ele
          p.hint.option-hint(v-if="!barcodeStatus.eligible") {{ barcodeStatus.reason }}

    span(slot="footer")
      el-button(@click="dialog.visible = false") Cancelar
      el-button(type="primary" :disabled="!dialog.productId" :loading="saving" @click="confirmMatch") Vincular

</template>
<script setup>
import {
  computed, getCurrentInstance, nextTick, onMounted, ref, watch,
} from 'vue'
import { useRouter } from 'vue2-helpers/vue-router'

import SnowflakeSearchList from '@/components/list/SearchList.vue'
import PurchaseInvoiceItemService from '@/services/fiscal/PurchaseInvoiceItemService'
import ProductService from '@/services/stock/ProductService'
import { useMessage } from '@/utils/Message'
import {
  formatCest, formatCurrency, formatDate, formatNcm, formatNumber, isDoorToDoorCest, isProductGtin,
} from '../purchase-invoice/labels'

const router = useRouter()
const message = useMessage()
const confirm = getCurrentInstance().proxy.$confirm

const pendingService = { search: PurchaseInvoiceItemService.searchPending }

const list = ref()
const divergenceTable = ref()
const tab = ref('pending')
const rematching = ref(false)
const saving = ref(false)
const divergences = ref([])
const loadingDivergences = ref(false)
const applyingNcm = ref(null)
const keepingNcm = ref(null)

const emptyDialog = () => ({
  visible: false,
  row: null,
  suggestions: [],
  loadingSuggestions: false,
  productOptions: [],
  searching: false,
  productId: null,
  units: 1,
  remember: true,
  addBarcode: true,
  // Products that already carry the item's barcode
  barcodeOwners: [],
})
const dialog = ref(emptyDialog())

const describeError = error => error.description || error.message

// Whether the item's barcode can go on the chosen product, and why not when it can't
const barcodeStatus = computed(() => {
  const { row, productId, units, barcodeOwners } = dialog.value
  const ean = row && row.item.ean
  const owner = barcodeOwners.find(it => it.id !== productId)

  if (!ean) return { eligible: false, reason: '' }
  if (!isProductGtin(ean)) return { eligible: false, reason: `${ean} não é um código de barras válido de produto.` }
  if (Number(units) !== 1) {
    return { eligible: false, reason: `Com ${units} unidades por ${row.item.unit}, ${ean} é o código da embalagem, não do produto.` }
  }
  if (barcodeOwners.some(it => it.id === productId)) return { eligible: false, reason: 'O produto já tem este código de barras.' }
  if (owner) return { eligible: false, reason: `Este código de barras já está no produto ${owner.id} · ${owner.name}.` }

  return { eligible: true, reason: '' }
})

const findBarcodeOwners = async (ean) => {
  try {
    const result = await ProductService.search({ query: ean, pageSize: 20 })
    dialog.value.barcodeOwners = result.results.filter(it => (it.barcodes || []).includes(ean))
  } catch (error) {
    message.error(`Não foi possível verificar o código de barras: ${describeError(error)}`)
  }
}

const describeMatch = (response) => {
  const others = response.matchedByBarcode ? ` e mais ${response.matchedByBarcode} itens vinculados por ele` : ''

  if (response.barcode === 'ADDED') return `Item vinculado. Código de barras adicionado ao produto${others}.`
  if (response.barcode === 'ON_ANOTHER_PRODUCT') {
    return `Item vinculado. O código de barras não foi adicionado: ele já está no produto ${response.barcodeOwnerProductId}.`
  }
  return 'Item vinculado.'
}

const back = () => router.back()

const productLabel = product => [product.id, product.name, product.brandName].filter(Boolean).join(' · ')

const describeSuggestion = (suggestion) => {
  if (suggestion.source === 'BARCODE') return 'mesmo código de barras'
  const units = Number(suggestion.unitsPerSupplierUnit)
  const perBox = units > 1 ? `, ${units} por unidade da nota` : ''
  return `pedido: ${formatNumber(suggestion.orderQuantity)} × ${formatCurrency(suggestion.orderUnitPrice)}${perBox}`
}

const loadDivergences = async () => {
  loadingDivergences.value = true

  try {
    divergences.value = await PurchaseInvoiceItemService.ncmDivergences()
  } catch (error) {
    message.error(`Não foi possível carregar as divergências de NCM: ${describeError(error)}`)
  } finally {
    loadingDivergences.value = false
  }
}

const refresh = async () => {
  list.value.reload()
  await loadDivergences()
}

const rematch = async () => {
  rematching.value = true

  try {
    const result = await PurchaseInvoiceItemService.matchPending()
    message.success(`${result.matched} itens vinculados.`)
    await refresh()
  } catch (error) {
    message.error(describeError(error))
  } finally {
    rematching.value = false
  }
}

const openMatchDialog = async (row) => {
  dialog.value = { ...emptyDialog(), visible: true, row, loadingSuggestions: true }

  if (row.item.ean) findBarcodeOwners(row.item.ean)

  try {
    const suggestions = await PurchaseInvoiceItemService.suggestions(row.item.id)
    dialog.value.suggestions = suggestions
    dialog.value.productOptions = suggestions.map(it => ({ id: it.productId, name: it.productName, brandName: it.brandName }))
  } catch (error) {
    message.error(`Não foi possível carregar as sugestões: ${describeError(error)}`)
  } finally {
    dialog.value.loadingSuggestions = false
  }
}

const pickSuggestion = (suggestion) => {
  dialog.value.units = Number(suggestion.unitsPerSupplierUnit) || 1
}

const searchProducts = async (query) => {
  if (!query) return

  dialog.value.searching = true

  try {
    const result = await ProductService.search({ query, pageSize: 20 })
    dialog.value.productOptions = result.results.map(it => ({ id: it.id, name: it.name, brandName: it.brandName }))
  } catch (error) {
    message.error(describeError(error))
  } finally {
    dialog.value.searching = false
  }
}

const confirmMatch = async () => {
  const {
    row, productId, units, remember, addBarcode,
  } = dialog.value
  saving.value = true

  try {
    const response = await PurchaseInvoiceItemService.match(row.item.id, {
      productId,
      unitsPerSupplierUnit: units,
      rememberSupplierCode: remember,
      addBarcode: addBarcode && barcodeStatus.value.eligible,
    })
    message.success(describeMatch(response))
    dialog.value.visible = false
    await refresh()
  } catch (error) {
    message.error(describeError(error))
  } finally {
    saving.value = false
  }
}

const ignore = async (row) => {
  try {
    await confirm(
      `Marcar "${row.item.description}" como não sendo produto de estoque? `
      + `Os itens com o código ${row.item.supplierCode} deste fornecedor também serão ignorados.`,
    )
  } catch (cancelled) {
    return
  }

  try {
    await PurchaseInvoiceItemService.ignore(row.item.id, true)
    message.success('Item marcado como não sendo produto de estoque.')
    list.value.reload()
  } catch (error) {
    message.error(describeError(error))
  }
}

const disregard = async (row) => {
  try {
    await confirm(
      `Desconsiderar "${row.item.description}" da NF ${row.invoiceNumber}? O item não conta em nenhum mês e nada é `
      + 'lembrado sobre o código do fornecedor, ao contrário de "Não é produto de estoque".',
    )
  } catch (cancelled) {
    return
  }

  try {
    await PurchaseInvoiceItemService.disregard(row.item.id)
    message.success('Item desconsiderado.')
    list.value.reload()
  } catch (error) {
    message.error(describeError(error))
  }
}

const useInvoiceNcm = async (row) => {
  applyingNcm.value = row.productId

  try {
    if (isDoorToDoorCest(row.invoiceCest)) {
      await ProductService.update(row.productId, { ncm: row.invoiceNcm })
      message.warning(
        `NCM do produto ${row.productId} atualizado para ${formatNcm(row.invoiceNcm)}. O CEST da nota `
        + `(${formatCest(row.invoiceCest)}) é de venda porta a porta e não foi aplicado: ajuste o CEST no cadastro.`,
      )
    } else {
      // The CEST belongs to the NCM, so the product's old one can't stay; an empty CEST clears it
      await ProductService.update(row.productId, { ncm: row.invoiceNcm, cest: row.invoiceCest || '' })
      const cest = row.invoiceCest ? `CEST ${formatCest(row.invoiceCest)}` : 'sem CEST'
      message.success(`Produto ${row.productId} atualizado: NCM ${formatNcm(row.invoiceNcm)}, ${cest}.`)
    }
    await loadDivergences()
  } catch (error) {
    message.error(describeError(error))
  } finally {
    applyingNcm.value = null
  }
}

const keepProductNcm = async (row) => {
  try {
    await confirm(
      `Manter o NCM ${formatNcm(row.productNcm)} no produto ${row.productId}? Notas com o NCM `
      + `${formatNcm(row.invoiceNcm)} deixam de apontar divergência para ele; uma nota com outro NCM volta a apontar.`,
    )
  } catch (cancelled) {
    return
  }

  keepingNcm.value = row.productId

  try {
    await PurchaseInvoiceItemService.keepProductNcm(row.productId, row.invoiceNcm)
    message.success(`NCM ${formatNcm(row.productNcm)} mantido no produto ${row.productId}.`)
    await loadDivergences()
  } catch (error) {
    message.error(describeError(error))
  } finally {
    keepingNcm.value = null
  }
}

// Laid out while its tab is hidden, the table takes itself for one that scrolls sideways and leaves its fixed column a
// scrollbar short of the rows once shown, cropping the buttons; laying it out again with the tab visible corrects it
watch(tab, async (value) => {
  if (value !== 'ncm') return

  await nextTick()
  divergenceTable.value.doLayout()
})

onMounted(loadDivergences)
</script>
<style lang="scss" scoped>
.hint {
  margin: 0 0 8px;
  color: #5e7382;
}

h5 {
  margin: 16px 0 8px;
}
.suggestions {
  display: flex;
  flex-direction: column;
  gap: 6px;

  .suggestion-detail {
    margin-left: 8px;
    color: #5e7382;
  }
}

.units {
  margin-top: 16px;

  .option + .option {
    margin-top: 8px;
  }

  .option-hint {
    margin: 4px 0 0 24px;
  }
}

.option-brand {
  float: right;
  margin-left: 16px;
  color: #8492a6;
  font-size: 13px;
}
</style>
